import React, { useState, useRef, useEffect } from 'react';
import BaseModal from '../../modals/BaseModal/BaseModal';
import EventForm from '../../forms/EventForm/EventForm';
import AssignmentForm from '../../forms/AssignmentForm/AssignmentForm';
import { trackEvent } from '../../../utils/analytics';
import './AddToCalendarModal.css';

/**
 * AddToCalendarModal - Multi-step wizard for creating events or assignments
 * 
 * Step 1: Choose between Event or Assignment
 * Step 2: Show appropriate form (EventForm or AssignmentForm)
 * 
 * Features:
 * - Dirty state tracking with discard confirmation
 * - Context preservation when switching types
 * - Form remounting via key changes
 * - Analytics tracking
 */
export default function AddToCalendarModal({
  isOpen,
  onClose,
  prefill = {},
  restoreFocusEl,
  onCreated,
  onSuccess, // Support both prop names for backward compatibility
  initialStep = 'choose' // 'choose' | 'event' | 'assignment'
}) {
  const [step, setStep] = useState(initialStep);
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pendingStep, setPendingStep] = useState(null);
  const [formInstanceKey, setFormInstanceKey] = useState(0);
  const submitButtonRef = useRef(null);
  const cancelFooterButtonRef = useRef(null);
  
  // Store original prefill to preserve context when switching types
  const basePrefillRef = useRef(prefill);
  const cancelButtonRef = useRef(null);

  // Force pill shape on buttons after mount
  useEffect(() => {
    if (submitButtonRef.current) {
      submitButtonRef.current.style.borderRadius = '9999px';
    }
    if (cancelFooterButtonRef.current) {
      cancelFooterButtonRef.current.style.borderRadius = '9999px';
    }
  }, [step]);

  // Update basePrefillRef when modal opens
  useEffect(() => {
    if (isOpen) {
      basePrefillRef.current = prefill;
      setStep(initialStep);
    }
  }, [isOpen, prefill, initialStep]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep(initialStep);
      setIsDirty(false);
      setShowDiscardConfirm(false);
      setPendingStep(null);
      setFormInstanceKey(0);
    }
  }, [isOpen, initialStep]);

  // Track step changes
  const changeStep = (newStep) => {
    trackEvent?.('calendar_modal_step_change', { from: step, to: newStep });
    setStep(newStep);
  };

  // Handle back/change type button
  const handleBack = () => {
    if (isDirty) {
      setPendingStep('choose');
      setShowDiscardConfirm(true);
      // Focus Cancel button when confirmation appears
      setTimeout(() => cancelButtonRef.current?.focus(), 50);
    } else {
      changeStep('choose');
    }
  };

  // Handle close button
  const handleClose = () => {
    // If on a form step with unsaved changes, show discard confirmation
    if ((step === 'event' || step === 'assignment') && isDirty) {
      setPendingStep('close');
      setShowDiscardConfirm(true);
      // Focus Cancel button when confirmation appears
      setTimeout(() => cancelButtonRef.current?.focus(), 50);
      return;
    }
    
    trackEvent?.('calendar_modal_close', {
      step,
      typeSelected: step !== 'choose' ? step : null
    });
    onClose();
  };

  // Handle discard confirmation
  const handleDiscardConfirm = () => {
    // Increment key to fully remount the form
    setFormInstanceKey(k => k + 1);
    setIsDirty(false);
    setShowDiscardConfirm(false);
    
    // If pending step is 'close', close the modal
    if (pendingStep === 'close') {
      trackEvent?.('calendar_modal_close', {
        step,
        typeSelected: step,
        discarded: true
      });
      onClose();
      return;
    }
    
    // Switch to pending step
    if (pendingStep) {
      changeStep(pendingStep);
      setPendingStep(null);
    }
  };

  const handleDiscardCancel = () => {
    setShowDiscardConfirm(false);
    setPendingStep(null);
  };

  // Handle choice card clicks
  const handleChoiceClick = (type) => {
    changeStep(type);
  };

  // Handle form dirty state changes
  const handleDirtyChange = (dirty) => {
    setIsDirty(dirty);
  };

  // Handle successful creation
  const handleSuccess = (item, type) => {
    const callback = onCreated || onSuccess;
    if (callback) {
      callback({ type, item });
    }
    onClose();
  };

  // Build prefill for forms (preserve endeavorId and selectedDate across type switches)
  const buildFormPrefill = () => {
    const base = {
      selectedDate: basePrefillRef.current.selectedDate,
      endeavorId: basePrefillRef.current.endeavorId
    };

    if (step === 'event') {
      return {
        ...base,
        defaultEventType: basePrefillRef.current.defaultEventType || 'lecture'
      };
    } else if (step === 'assignment') {
      return {
        ...base,
        // Only pass eventId to assignment when switching from event context
        eventId: basePrefillRef.current.eventId,
        dueDate: basePrefillRef.current.dueDate,
        dueTime: basePrefillRef.current.dueTime
      };
    }

    return base;
  };

  // Dynamic title
  const getTitle = () => {
    if (step === 'choose') return 'Add to Calendar';
    if (step === 'event') return 'Create Event';
    if (step === 'assignment') return 'Create Assignment';
    return '';
  };
  
  // Build custom header
  const renderHeader = () => (
    <div className="modal-header-premium">
      <div className="modal-header-content">
        {step !== 'choose' && (
          <button
            type="button"
            className="btn-header-back"
            onClick={handleBack}
            aria-label="Go back"
          >
            ← Back
          </button>
        )}
        {step === 'choose' && <div className="header-spacer" />}
        <h2 className="modal-title-premium">{getTitle()}</h2>
        <button
          type="button"
          className="btn-header-close"
          onClick={handleClose}
          aria-label="Close"
        >
          ×
        </button>
      </div>
    </div>
  );
  
  // Build footer with actions (only for form steps)
  const renderFooter = () => {
    if (step === 'choose' || showDiscardConfirm) return null;
    
    return (
      <div className="modal-footer-actions">
        <button
          ref={cancelFooterButtonRef}
          type="button"
          className="btn-secondary"
          onClick={handleClose}
        >
          Cancel
        </button>
        <button
          ref={submitButtonRef}
          type="submit"
          form={step === 'event' ? 'event-form' : 'assignment-form'}
          className="btn-primary"
        >
          {step === 'event' ? '✨ Create Event' : '✨ Create Assignment'}
        </button>
      </div>
    );
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      restoreFocusEl={restoreFocusEl}
      className="add-to-calendar-modal"
      maxWidth="920px"
      showHeader={false}
      footer={renderFooter()}
    >
      {renderHeader()}
      
      <div className="add-to-calendar-body">
        {/* Discard Confirmation Panel */}
        {showDiscardConfirm && (
          <div className="add-to-calendar-discard-confirm">
            <h3 className="discard-confirm-title">Discard changes?</h3>
            <p className="discard-confirm-body">
              You have unsaved changes. Switching will clear what you've entered.
            </p>
            <div className="discardActions">
              <button
                ref={cancelButtonRef}
                type="button"
                className="btnDiscardCancel"
                onClick={handleDiscardCancel}
                style={{ borderRadius: '9999px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btnDiscardConfirm"
                onClick={handleDiscardConfirm}
                style={{ borderRadius: '9999px' }}
              >
                Discard & Switch
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Choice Cards */}
        {step === 'choose' && !showDiscardConfirm && (
          <div className="addChoiceGrid">
            <button
              type="button"
              className="addChoiceCard"
              onClick={() => handleChoiceClick('event')}
              aria-pressed={false}
            >
              <div className="addChoiceIconWrap">
                📅
              </div>
              <div className="addChoiceText">
                <div className="addChoiceTitle">Event</div>
                <div className="addChoiceSubtitle">
                  Calendar event with date, time, and location
                </div>
              </div>
            </button>

            <button
              type="button"
              className="addChoiceCard"
              onClick={() => handleChoiceClick('assignment')}
              aria-pressed={false}
            >
              <div className="addChoiceIconWrap">
                📋
              </div>
              <div className="addChoiceText">
                <div className="addChoiceTitle">Assignment</div>
                <div className="addChoiceSubtitle">
                  Assignment with due date linked to event
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Step 2: Forms */}
        {step === 'event' && !showDiscardConfirm && (
          <EventForm 
            key={`event-${formInstanceKey}`} 
            prefill={buildFormPrefill()} 
            compact 
            onCancel={onClose} 
            onSuccess={(item) => handleSuccess(item, 'event')} 
            onDirtyChange={handleDirtyChange}
            renderActions={false}
          />
        )}

        {step === 'assignment' && !showDiscardConfirm && (
          <AssignmentForm 
            key={`assignment-${formInstanceKey}`} 
            prefill={buildFormPrefill()} 
            compact 
            onCancel={onClose} 
            onSuccess={(item) => handleSuccess(item, 'assignment')} 
            onDirtyChange={handleDirtyChange}
            renderActions={false}
          />
        )}
      </div>
    </BaseModal>
  );
}
